import { Transaction } from '../types';

interface RecurringTransaction {
  description: string;
  frequency: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  averageAmount: number;
  firstDate: string;
  lastDate: string;
  occurrences: Transaction[];
  isSubscription: boolean;
  confidence: number;
}

function isLikelySubscription(
  transactions: Transaction[],
  averageAmount: number,
  dates: string[]
): boolean {
  // Check if all transactions are debits
  const allDebits = transactions.every(t => t.type === 'debit');
  if (!allDebits) return false;

  // Check if amount variation is small (within 10% of average)
  const amountVariation = transactions.every(t => 
    Math.abs(t.amount - averageAmount) / averageAmount <= 0.10
  );

  // Check if dates are roughly monthly (within 5 days of same day each month)
  const monthlyPattern = dates.every((date, i) => {
    if (i === 0) return true;
    const prevDate = new Date(dates[i - 1]);
    const currDate = new Date(date);
    const dayDiff = Math.abs(currDate.getDate() - prevDate.getDate());
    return dayDiff <= 5;
  });

  // Check for common subscription keywords
  const subscriptionKeywords = [
    'subscription', 'monthly', 'recurring', 'membership', 'premium',
    'netflix', 'spotify', 'amazon prime', 'hulu', 'disney+', 'hbo',
    'gym', 'fitness', 'yoga', 'classpass', 'audible', 'apple',
    'google', 'microsoft', 'adobe', 'dropbox', 'box', 'slack',
    'zoom', 'github', 'gitlab', 'bitbucket', 'aws', 'azure',
    'digitalocean', 'heroku', 'vercel', 'netlify', 'cloudflare',
    'rent', 'mortgage', 'insurance', 'utility', 'phone', 'internet',
    'cable', 'tv', 'streaming', 'music', 'software', 'saas',
    'cloud', 'hosting', 'domain', 'website', 'app', 'service'
  ];

  const hasSubscriptionKeyword = transactions.some(t =>
    subscriptionKeywords.some(keyword =>
      t.description.toLowerCase().includes(keyword)
    )
  );

  // Calculate confidence score
  const confidence = calculateConfidence(transactions, averageAmount, dates);

  // Consider it a subscription if it has high confidence (85% or more)
  // or if it matches the basic subscription criteria
  return confidence >= 85 || (amountVariation && monthlyPattern && hasSubscriptionKeyword);
}

function calculateConfidence(
  transactions: Transaction[],
  averageAmount: number,
  dates: string[]
): number {
  let confidence = 0;

  // Amount consistency (up to 40 points)
  const amountVariations = transactions.map(t => 
    Math.abs(t.amount - averageAmount) / averageAmount
  );
  const amountConsistency = 1 - (Math.max(...amountVariations) - Math.min(...amountVariations));
  confidence += amountConsistency * 40;

  // Date consistency (up to 30 points)
  const dateDeviations = dates.slice(1).map((date, i) => {
    const prevDate = new Date(dates[i]);
    const currDate = new Date(date);
    return Math.abs(currDate.getDate() - prevDate.getDate());
  });
  const dateConsistency = 1 - (Math.max(...dateDeviations) / 31);
  confidence += dateConsistency * 30;

  // Number of occurrences (up to 30 points)
  const occurrenceScore = Math.min(transactions.length / 6, 1);
  confidence += occurrenceScore * 30;

  return Math.round(confidence);
}

export function findRecurringTransactions(transactions: Transaction[]): RecurringTransaction[] {
  const groups: Record<string, Transaction[]> = {};
  const recurringTransactions: RecurringTransaction[] = [];

  // Group transactions by description
  transactions.forEach(transaction => {
    const key = transaction.description.toLowerCase().trim();
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(transaction);
  });

  // Analyze each group
  Object.entries(groups).forEach(([description, groupTransactions]) => {
    if (groupTransactions.length < 2) return;

    // Sort by date
    const sortedTransactions = [...groupTransactions].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    const dates = sortedTransactions.map(t => t.date);
    const amounts = sortedTransactions.map(t => t.amount);
    const averageAmount = amounts.reduce((a, b) => a + b, 0) / amounts.length;

    // Calculate frequency
    const firstDate = new Date(dates[0]);
    const lastDate = new Date(dates[dates.length - 1]);
    const daysBetween = (lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24);
    const avgDaysBetween = daysBetween / (dates.length - 1);

    let frequency: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
    if (avgDaysBetween <= 10) frequency = 'weekly';
    else if (avgDaysBetween <= 45) frequency = 'monthly';
    else if (avgDaysBetween <= 120) frequency = 'quarterly';
    else frequency = 'yearly';

    // Check if it's likely a subscription
    const isSubscription = isLikelySubscription(
      sortedTransactions,
      averageAmount,
      dates
    );

    // Calculate confidence score
    const confidence = calculateConfidence(
      sortedTransactions,
      averageAmount,
      dates
    );

    recurringTransactions.push({
      description,
      frequency,
      averageAmount,
      firstDate: dates[0],
      lastDate: dates[dates.length - 1],
      occurrences: sortedTransactions,
      isSubscription,
      confidence
    });
  });

  // Sort by confidence and subscription status
  return recurringTransactions.sort((a, b) => {
    // Prioritize subscriptions
    if (a.isSubscription && !b.isSubscription) return -1;
    if (!a.isSubscription && b.isSubscription) return 1;
    // Then sort by confidence
    return b.confidence - a.confidence;
  });
} 